import React, { useEffect, useState } from "react";
import { Button, Upload, message, Modal, Spin } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import axios from "axios";
import { downloadUrlApi, getUploadUrlApi } from "../../services/common.services";
import { convertToFormData } from "../../helpers/utility";
import { getDefaultUploadFileName } from "../../helpers/galleryUtils";

const MAX_SIZE_MB = 30;
const ACCEPTED_EXT = new Set(["png", "jpg", "jpeg", "svg", "pdf"]);
const CONTROL_WIDTH = 340;

const isAcceptedFile = (file) => {
  const ext = (file.name || "").split(".").pop()?.toLowerCase();
  return (
    ACCEPTED_EXT.has(ext) ||
    file.type === "application/pdf" ||
    file.type?.startsWith("image/")
  );
};

const fileLabel = (value) =>
  value?.file_name || value?.name || value?.path?.split("/").pop() || "Uploaded file";

const isPdfFile = (value) => {
  if (value?.type === "application/pdf") return true;
  return (fileLabel(value) || "").toLowerCase().endsWith(".pdf");
};

const ClinicalFormFileUpload = ({
  value,
  onChange,
  readOnly = false,
  admissionId,
  admissionType = "IP",
}) => {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState({ open: false, url: null, loading: false });

  const resolveFileUrl = async (path) => {
    const res = await downloadUrlApi(
      `path_key=${encodeURIComponent(path)}`
    );
    return res?.data;
  };

  useEffect(() => {
    return () => {
      // no blob URLs to revoke here
    };
  }, []);

  const customRequest = async ({ file, onSuccess, onError }) => {
    if (!admissionId) {
      message.error("Admission context is missing for file upload");
      onError(new Error("Missing admission"));
      return;
    }
    if (!isAcceptedFile(file)) {
      message.error(`${file.name} is not a supported file type`);
      onError(new Error("Invalid file type"));
      return;
    }
    if (file.size / 1024 / 1024 > MAX_SIZE_MB) {
      message.error(`File must be ${MAX_SIZE_MB}MB or smaller`);
      onError(new Error("File too large"));
      return;
    }

    setUploading(true);

    try {
      const res = await getUploadUrlApi(
        `path_key=${encodeURIComponent(file.name)}&admission_type=${admissionType}&admission_id=${admissionId}`
      );
      const info = {
        file: {
          ...file,
          originFileObj: file,
        },
      };
      const formData = convertToFormData(res, info);
      await axios({
        method: "POST",
        url: res?.data?.url,
        data: formData,
        headers: { "Content-Type": "multipart/form-data" },
      });

      const uploaded = {
        path: res?.data?.fields?.key,
        type: file.type,
        file_name: getDefaultUploadFileName(file),
      };
      onChange(uploaded);
      onSuccess(uploaded);
      message.success("File uploaded");
    } catch (err) {
      message.error(err?.response?.data?.message || "File upload failed");
      onError(err);
    } finally {
      setUploading(false);
    }
  };

  const removeFile = () => onChange(null);

  const openPreview = async () => {
    if (!value?.path) return;
    setPreview({ open: true, url: null, loading: true });
    try {
      const url = await resolveFileUrl(value.path);
      setPreview({ open: true, url, loading: false });
    } catch (err) {
      message.error("Unable to open file");
      setPreview({ open: false, url: null, loading: false });
    }
  };

  const handleDownload = async () => {
    if (!value?.path) return;
    try {
      const url = preview.url || (await resolveFileUrl(value.path));
      const link = document.createElement("a");
      link.href = url;
      link.download = fileLabel(value);
      link.rel = "noopener noreferrer";
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      message.error("Unable to download file");
    }
  };

  const handlePrint = async () => {
    if (!value?.path) return;
    try {
      const url = preview.url || (await resolveFileUrl(value.path));
      const win = window.open(url, "_blank", "noopener,noreferrer");
      if (win) {
        win.addEventListener("load", () => {
          try {
            win.print();
          } catch (_) {
            /* some browsers block print on cross-origin PDFs */
          }
        });
      }
    } catch (err) {
      message.error("Unable to print file");
    }
  };

  if (value?.path) {
    return (
      <div style={{ width: CONTROL_WIDTH, maxWidth: "100%" }}>
        <Upload
          fileList={[
            {
              uid: value.path,
              name: fileLabel(value),
              status: "done",
              // url enables Ant Design download / preview affordances
              url: undefined,
            },
          ]}
          showUploadList={{
            showRemoveIcon: !readOnly,
            showDownloadIcon: true,
            showPreviewIcon: true,
          }}
          onPreview={openPreview}
          onDownload={handleDownload}
          onRemove={() => {
            removeFile();
            return true;
          }}
          openFileDialogOnClick={false}
          style={{ width: "100%" }}
        />
        <Modal
          title={fileLabel(value)}
          open={preview.open}
          onCancel={() => setPreview({ open: false, url: null, loading: false })}
          width="85%"
          destroyOnClose
          footer={[
            <Button key="download" onClick={handleDownload}>
              Download
            </Button>,
            <Button key="print" onClick={handlePrint}>
              Print
            </Button>,
            <Button
              key="open"
              type="primary"
              onClick={async () => {
                try {
                  const url =
                    preview.url || (await resolveFileUrl(value.path));
                  window.open(url, "_blank", "noopener,noreferrer");
                } catch (err) {
                  message.error("Unable to open file");
                }
              }}
            >
              Open
            </Button>,
          ]}
        >
          {preview.loading || !preview.url ? (
            <div style={{ display: "flex", justifyContent: "center", padding: 40 }}>
              <Spin />
            </div>
          ) : isPdfFile(value) ? (
            <iframe
              src={preview.url}
              title={fileLabel(value)}
              style={{ width: "100%", height: "70vh", border: "none" }}
            />
          ) : (
            <div style={{ textAlign: "center" }}>
              <img
                src={preview.url}
                alt={fileLabel(value)}
                style={{ maxWidth: "100%", maxHeight: "70vh" }}
              />
            </div>
          )}
        </Modal>
      </div>
    );
  }

  return (
    <div style={{ width: CONTROL_WIDTH, maxWidth: "100%" }}>
      <Upload
        accept=".png,.jpg,.jpeg,.svg,.pdf,application/pdf,image/*"
        maxCount={1}
        showUploadList
        disabled={readOnly || uploading}
        customRequest={customRequest}
      >
        <Button icon={<UploadOutlined />} loading={uploading} disabled={readOnly}>
          Upload file
        </Button>
      </Upload>
    </div>
  );
};

export default ClinicalFormFileUpload;
